import { IncomeTestAdjustmentRequest } from '@interfaces/partner-income-tax/requests/partner-income-tax.request';
import {
  IncomeTestDto,
  IncomeTestFieldKey,
} from '@interfaces/partner-income-tax/responses/partner-income-tax.response';

/**
 * Teste de renda ("Renda que sobra após o crescimento patrimonial", card 3515).
 *
 * Exceção consciente à régua "cálculo no backend": a tela recalcula LOCALMENTE só a prévia instantânea enquanto
 * o analista digita (pedido do protótipo: o resultado muda a cada tecla). A fórmula é a soma de cinco campos que
 * o próprio backend devolve, sem taxa, data nem constante de mercado. Ao sair do campo o PUT grava, e o número
 * que fica na tela passa a ser o devolvido pelo backend (`PartnerIncomeTaxService`), que é a fonte da verdade.
 *
 *   Recursos disponíveis = tributáveis líquidos + isentos + exclusiva (líquidos) − pagamentos − custo de vida
 *   Renda que sobra      = recursos disponíveis − acréscimo patrimonial
 */

/** Ordem dos campos na tela. */
export const INCOME_TEST_FIELD_KEYS: readonly IncomeTestFieldKey[] = [
  'taxableNet',
  'exempt',
  'exclusiveNet',
  'paymentsMade',
  'livingCost',
];

export interface IncomeTestFieldMeta {
  key: IncomeTestFieldKey;
  label: string;
  /** Rótulo quando o valor não foi ajustado. */
  originLabel: string;
}

export const INCOME_TEST_FIELDS: readonly IncomeTestFieldMeta[] = [
  { key: 'taxableNet', label: '+ Tributáveis líquidos de IR', originLabel: 'Da declaração' },
  { key: 'exempt', label: '+ Isentos e não tributáveis', originLabel: 'Da declaração' },
  { key: 'exclusiveNet', label: '+ Tributação exclusiva (líquidos)', originLabel: 'Da declaração' },
  { key: 'paymentsMade', label: '− Pagamentos declarados', originLabel: 'Da declaração' },
  { key: 'livingCost', label: '− Custo de vida estimado', originLabel: 'Premissa do analista' },
];

/** Valores em reais de cada campo, como estão na tela. `null` = vazio (conta como zero). */
export type IncomeTestValues = Record<IncomeTestFieldKey, number | null>;

/** Valores atuais (ajustados ou da declaração) do DTO. */
export function incomeTestValuesFrom(test: IncomeTestDto): IncomeTestValues {
  const values = {} as IncomeTestValues;
  for (const key of INCOME_TEST_FIELD_KEYS) {
    const field = test.fields[key];
    values[key] = field.value ?? field.declared ?? null;
  }
  return values;
}

function n(value: number | null | undefined): number {
  return value == null || !Number.isFinite(value) ? 0 : value;
}

export function availableResources(values: IncomeTestValues): number {
  return (
    n(values.taxableNet) +
    n(values.exempt) +
    n(values.exclusiveNet) -
    n(values.paymentsMade) -
    n(values.livingCost)
  );
}

/** Renda que sobra: positiva explica o crescimento; negativa é acréscimo a descoberto. */
export function incomeTestResult(values: IncomeTestValues, netWorthIncrease: number): number {
  return availableResources(values) - n(netWorthIncrease);
}

/**
 * O valor na tela é diferente do que está gravado (ajuste ou declaração)? Decide se a saída do campo grava e se
 * o campo já mostra "Ajustado" antes do PUT voltar. Vazio e zero são valores diferentes: vazio no custo de vida é
 * "premissa não informada".
 */
export function isFieldChanged(
  test: IncomeTestDto,
  key: IncomeTestFieldKey,
  value: number | null
): boolean {
  const field = test.fields[key];
  const saved = field.value ?? field.declared ?? null;
  return n(saved) !== n(value) || (saved == null) !== (value == null);
}

/** Marca "Ajustado": o backend já gravou ajuste, ou o analista acabou de mudar o valor. */
export function isFieldAdjusted(
  test: IncomeTestDto,
  key: IncomeTestFieldKey,
  value: number | null
): boolean {
  return test.fields[key].adjusted || isFieldChanged(test, key, value);
}

/** Corpo do PUT com só o campo que mudou. */
export function buildIncomeTestAdjustment(
  key: IncomeTestFieldKey,
  value: number | null
): IncomeTestAdjustmentRequest {
  return { [key]: value } as IncomeTestAdjustmentRequest;
}
